import { DetailBox, Icon, TextAreaInput, TextInput } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import { SecureResponseFile } from '../../../components/AzureFilesUploader/types';
import GoBackToListButton from '../../../components/buttons/GoBackToListButton';
import { PathsLayouts } from '../../../config/routes';
import ValidationFileChip from '../../../views/CourseValidationInternalStudent/parts/ModalCreateValidation/ValidationFileChip';
import { useStudentRemovalRequestTranslation } from '../hooks/translation';
import { StudentsRemovalRequestView } from '../types';
import BadgeState from '../../../components/badgeStatus/BadgeState';

type StudentRemovalRequestViewProps = {
  data: StudentsRemovalRequestView;
};

export function StudentRemovalRequestView({
  data,
}: StudentRemovalRequestViewProps) {
  const { removalRequest, student } = data;
  const { t } = useStudentRemovalRequestTranslation();
  const texts = useMemo(() => {
    return t<
      string,
      {
        title: string;
        body: string;
        studentColumn: string;
        idColumn: string;
        studyPlanVersionColumn: string;
        requestState: string;
        studyPlanVersion: string;
        type: string;
        comment: string;
        complementaryFiles: string;
        complementaryFilesZero: string;
        requestedBy: string;
        resolvedBy: string;
        requestResponse: string;
        requestResponsePending: string;
      }
    >('detail', {
      returnObjects: true,
    });
  }, [t]);

  const location = useLocation();
  const manageOrigin = location.pathname.includes(
    PathsLayouts.studentsRemovalRequestsManage,
  );
  const returnPath = manageOrigin
    ? `${PathsLayouts.studentsRemovalRequestsManage}#requests-resolved`
    : `${PathsLayouts.studentsRemovalRequest}#history`;

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }
    window.open(file?.url, '_blank');
  }, []);

  return (
    <>
      <Row>
        <Col xs="auto">
          <GoBackToListButton path={returnPath} />
        </Col>
        <Col xs={12} md={6} className="ml-auto">
          <DetailBox
            columns={[
              {
                title: texts.studentColumn,
                body: `${student.accountNames} ${student.accountLastNames}`,
                size: { xs: 12, md: 8 },
              },
              {
                title: texts.idColumn,
                body: student.identificationValueFormatted,
                size: { xs: 12, md: 4 },
              },
            ]}
          />
        </Col>
      </Row>
      <p className="fs-20 text-uppercase text-primary mb-0 fw-700">
        {texts.title}
      </p>
      <p>{texts.body}</p>
      <Row>
        <Col xs={12} md={6}>
          <TextInput
            label={texts.studyPlanVersion}
            name="studyPlanVersion"
            disabled
            value={data.studyPlanVersion.name}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            label={texts.type}
            name="removalReason"
            disabled
            value={removalRequest.type.name}
          />
        </Col>
      </Row>
      <p className="fs-16 text-uppercase text-primary mb-0 fw-700">
        {texts.comment}
      </p>
      <div className="mb-3">
        <TextAreaInput
          label=""
          name="comment"
          disabled
          value={removalRequest.comment}
        />
      </div>
      {texts.complementaryFiles}
      <div
        className="w-full mt-2 d-flex flex-wrap align-items-center justify-content-start"
        style={{ gap: 5, boxShadow: 'inherit' }}
      >
        {removalRequest.files.map((file: any, i: number) => (
          <ValidationFileChip
            key={file.id.toString()}
            value={i}
            children={`${file.name}.${file.extension}`}
            onDownload={() => handleDownloadFile(file)}
          />
        ))}
        {!removalRequest.files.length && (
          <p className="mb-0 w-100">{texts.complementaryFilesZero}</p>
        )}
      </div>
      <div className="d-flex align-items-center justify-content-start my-3">
        <Icon name="information" className="mr-3" />
        <span className="fw-700 mr-2">{texts.requestedBy}</span>
        <span>
          {removalRequest.requestedBy.accountNames}{' '}
          {removalRequest.requestedBy.accountLastNames}{' '}
          {dayjs(removalRequest.requestedAt).format('DD/MM/YYYY')}
        </span>
      </div>
      <hr />
      <Row>
        <Col>
          <p className="fs-16 text-uppercase text-primary mb-0 fw-700">
            {texts.requestResponse}
          </p>
        </Col>
        <Col xs="auto">
          <div className="d-flex align-items-center justify-content-start">
            <span className="mr-2">{texts.requestState}</span>
            <span>
              <BadgeState
                status={removalRequest.state}
                genderContext="female"
              />
            </span>
          </div>
        </Col>
      </Row>
      {removalRequest.response ? (
        <>
          <div className="mb-3">
            <TextAreaInput
              label=""
              name="responseComment"
              disabled
              value={removalRequest.response.comment}
            />
          </div>
          {texts.complementaryFiles}
          <div
            className="w-full mt-2 d-flex flex-wrap align-items-center justify-content-start"
            style={{ gap: 5, boxShadow: 'inherit' }}
          >
            {removalRequest.response.files.map((file: any, i: number) => (
              <ValidationFileChip
                key={file.id.toString()}
                value={i}
                children={`${file.name}.${file.extension}`}
                onDownload={() => handleDownloadFile(file)}
              />
            ))}
            {!removalRequest.response.files.length && (
              <p className="mb-0 w-100">{texts.complementaryFilesZero}</p>
            )}
          </div>
          <div className="d-flex align-items-center justify-content-start my-3">
            <Icon name="information" className="mr-3" />
            <span className="fw-700 mr-2">{texts.resolvedBy}</span>
            <span>
              {removalRequest.response.resolvedBy.accountNames}{' '}
              {removalRequest.response.resolvedBy.accountLastNames}{' '}
              {dayjs(removalRequest.response.resolvedAt).format('DD/MM/YYYY')}
            </span>
          </div>
        </>
      ) : (
        <p>{texts.requestResponsePending}</p>
      )}
    </>
  );
}
